<template>
  <fieldset>
    <legend :class="{ optional: optional }" class="fs-6 mb-2">
      {{ label }}
    </legend>
    <div class="row">
      <div class="p-inputgroup mb-2">
        <Select
          v-model="phone!.type"
          :class="{
            'p-invalid': phoneField.showError.value,
          }"
          :options="availableOptions"
          placeholder="Type"
          style="
            border-top-right-radius: 0px;
            border-bottom-right-radius: 0px;
            border-right-width: 1px;
            border-right-style: solid;
            border-right-color: var(--p-select-border-color);
          "
          @change="onChange"
        />
        <PTextfield
          v-model="phone!.num"
          style="border-bottom-left-radius: 0px; border-top-left-radius: 0px"
          :class="{
            'p-invalid': phoneField.showError.value,
          }"
          autocomplete="off"
          type="phonenumber"
          @blur="onBlur"
          @change="onChange"
        />
      </div>
      <HelpText
        help-text="Ex: +12222425, (404) 888-9999, etc"
        :touched="phoneField.touched.value"
        :error-message="phoneField.errorMessage.value"
      />
    </div>
  </fieldset>
</template>

<script setup lang="ts">
import PTextfield from "primevue/inputtext";
import Select from "primevue/select";
import { computed, nextTick, Ref, watch } from "vue";

import { useField, Validators } from "@/utilities/validations";

type Phone = { num: string; type: string | undefined };

interface Props {
  label: string;
  name: string;
  optional?: boolean;
  rules: {
    phone: Validators<Phone | null>;
  };
  formName: string;
}

const props = withDefaults(defineProps<Props>(), {
  optional: false,
});

const phone = defineModel<Phone | null>({
  default: () => ({ num: "", type: undefined }),
});

const options = defineModel<Record<string, boolean>>("options", {
  default: () => ({
    Cell: true,
    Home: true,
    Work: true,
    Other: true,
  }),
});

const availableOptions = computed(() => {
  return Object.entries(options.value)
    .filter(([option, active]) => {
      if (option === phone.value?.type) {
        return true;
      }

      return active;
    })
    .map(([option]) => option);
});

const phoneRules = computed(() => props.rules);

const phoneField = useField(
  props.name,
  phone as Ref<Phone | null>,
  phoneRules,
  {
    formName: props.formName,
  },
);

const onChange = async () => {
  phoneField.touched.value = true;
  await nextTick();
  phoneField.validate();
};

const onBlur = () => {
  phoneField.touched.value = true;
};

watch(
  () => phone.value?.num,
  (p) => {
    if (p == null || p.length == 0) {
      phone.value!.type = undefined;
    }
  },
);

watch(
  () => props.rules,
  () => {
    phoneField.validate();
  },
);
</script>

<style scoped lang="scss">
.p-inputwrapper {
  border-right: 0;
  width: 15ch;
  flex: unset !important;
}

legend.optional {
  &::after {
    content: " optional";
    color: #777;
    font-size: smaller;
  }
}
</style>
